// src/components/IDE/ide/core/runner.ts
import type { BreakpointAPI, Controls, Executable, LanguageAdapter, RunState } from './types';

class BreakpointRegistry implements BreakpointAPI {
    private set = new Set<number>(); // 1-based
    getAll() { return Array.from(this.set).sort().map(line => ({ line })); }
    has(line: number) { return this.set.has(line); }
    toggle(line: number) { if (this.set.has(line)) this.set.delete(line); else this.set.add(line); }
    clear() { this.set.clear(); }
}

export class Runner {
    private adapter: LanguageAdapter;
    private exec: Executable | null = null;
    private state: RunState = 'idle';

    private _paused = false;
    private _stop = false;
    private _ignoreBreakpoints = false;
    private _breakpoints = new BreakpointRegistry();

    // onStop hooks
    private _onStopCbs = new Set<() => void>();
    onStop(cb: () => void): () => void {
        this._onStopCbs.add(cb);
        return () => { this._onStopCbs.delete(cb); };
    }
    private _emitStop() { for (const cb of Array.from(this._onStopCbs)) { try { cb(); } catch { } } }

    constructor(adapter: LanguageAdapter) { this.adapter = adapter; }

    get runState(): RunState { return this.state; }

    controls: Controls = {
        isPaused: () => this._paused,
        requestPause: () => { this._paused = true; },
        shouldStop: () => this._stop,
        ignoreBreakpoints: () => this._ignoreBreakpoints,
        breakpoints: this._breakpoints,
    };

    setIgnoreBreakpoints(v: boolean) { this._ignoreBreakpoints = v; }

    setAdapter(adapter: LanguageAdapter) {
        this.adapter = adapter;
        this.exec = null;
        this._breakpoints.clear();
        this.state = 'idle';
    }

    async compile(code: string, bridge: any) {
        this.exec = await this.adapter.compile(code, bridge, this.controls);
        this.state = 'idle';
    }

    async runAll() {
        if (!this.exec) throw new Error('Nothing compiled');
        this._paused = false; this._stop = false; this.state = 'running';
        try {
            await this.adapter.runAll(this.exec, this.controls);
            this.state = this._stop ? 'stopped' : this._paused ? 'paused' : 'idle';
        } catch (e) {
            console.error(e);
            this.state = 'error';
            throw e;
        }
    }

    async step() {
        if (!this.exec) throw new Error('Nothing compiled');
        this._paused = false; this._stop = false; this.state = 'running';
        try {
            await this.adapter.runStep(this.exec, this.controls);
            this._paused = true; this.state = 'paused';
        } catch (e) {
            console.error(e);
            this.state = 'error';
            throw e;
        }
    }

    pause() { this._paused = true; if (this.exec) this.state = 'paused'; }

    stop() {
        this._stop = true;
        this._paused = false;
        if (this.exec) this.state = 'stopped';

        // 1) cleanup adapter/runtime
        if (this.adapter.stop && this.exec) this.adapter.stop(this.exec).catch(() => { });

        // 2) hook context runtime optionnel
        try {
            const ec = (this.exec as any)?.payload?.executionContext;
            ec?.__onStop?.();
        } catch { }

        // 3) notifier l’IDE
        this._emitStop();
    }

    breakpoints(): BreakpointAPI { return this._breakpoints; }
}
